import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { toast } from '@/app/components/ui/use-toast';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useNotificationTime } from '../hook/use-notification-time';
import type { SingleMessageItemProps } from '../types';

export const SingleMessageItem: FC<SingleMessageItemProps> = ({ data, onNotificationClick }) => {
  const t = useTranslations();
  const {
    id,
    sender_avatar,
    sender_id,
    sender_nick_name,
    sender_status,
    image_uri,
    created_at,
    type,
    obj_type,
    obj_id,
    obj_status,
    status,
    metadata = {},
  } = data;
  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));

  const relativeTimeText = useNotificationTime(created_at ?? '');

  const renderContent = () => {
    switch (type) {
      case 'like':
        return t('notifications.interaction.likes');
      case 'inspiration_recreated':
        return t('notifications.interaction.inspirationRecreated');
      case 'reference_used':
        return t('notifications.interaction.referencesUsed');
      case 'follow':
        return t('notifications.interaction.follow');
      default:
        return '';
    }
  };

  const getSubtype = () => {
    switch (type) {
      case 'like':
        return 'liked_messages';
      case 'inspiration_recreated':
        return 'recreated_messages';
      case 'reference_used':
        return 'reference_messages';
      case 'follow':
        return 'follow_message';
      default:
        return '';
    }
  };

  const handleClick = () => {
    onNotificationClick(id ?? '');

    reportEvent('notification_click', {
      id: id ?? '',
      type: 'interactive_massages',
      subtype: getSubtype(),
    });
    if (obj_status === 'invalid') {
      toast({
        type: 'error',
        message: t('notifications.interaction.invalid'),
      });
      return;
    }
    if (type === 'follow') {
      openOrRedirect(`/profile/${sender_id}`);
      return;
    }
    if (obj_type === 'share_element') {
      openOrRedirect(`/profile/${userInfo?.id}?obj_id=${metadata?.element_id}&obj_type=${obj_type}`);
    } else {
      openOrRedirect(`/profile/${userInfo?.id}?obj_id=${obj_id}&obj_type=${obj_type}`);
    }
  };

  const handleClickUser = () => {
    openOrRedirect(`/profile/${sender_id}`);
    onNotificationClick(id ?? '');
    reportEvent('notification_click', {
      id: id ?? '',
      type: 'interactive_massages',
      subtype: getSubtype(),
    });
  };

  const renderImage = () => {
    if (type === 'follow') {
      return null;
    }
    if (obj_status === 'invalid') {
      return (
        <div className="flex h-12 w-12 items-center justify-center rounded-8 bg-system-white10">
          <BaseLineExclamation className="size-5" />
        </div>
      );
    }
    if (metadata?.element_modality === 'text') {
      return (
        <TextElementCover
          name={metadata?.element_name || ''}
          textClassName="text-xs"
          className="h-12 w-12 overflow-hidden rounded-8 text-xs"
        />
      );
    }
    return (
      <div className={cn('h-12 w-12 overflow-hidden rounded-8')}>
        <img src={image_uri} alt="" className="h-full w-full object-cover" />
      </div>
    );
  };

  return (
    <div
      className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-8 p-2 hover:bg-system-white08"
      onClick={handleClick}
    >
      <div
        className="relative h-9 w-9"
        onClick={(e) => {
          e.stopPropagation();
          handleClickUser();
        }}
      >
        <div className="h-full w-full overflow-hidden rounded-full">
          <UserAvatar
            userInfo={{
              avatar: sender_avatar || '',
              created_at: '',
              gender: 'unspecified',
              id: sender_id || '',
              nickname: sender_nick_name || '',
              role: 'user',
            }}
          />
        </div>
        {status === 'unread' && (
          <div className="absolute top-0 right-0 h-[7px] w-[7px] rounded-full bg-system-error1" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 overflow-hidden text-base text-system-white">
        <div className="flex items-center gap-1">
          <div
            className="line-clamp-1 flex-shrink text-ellipsis whitespace-nowrap break-all font-semibold"
            onClick={(e) => {
              e.stopPropagation();
              handleClickUser();
            }}
          >
            {sender_status === 'invalid' ? t('notifications.userInvalid') : sender_nick_name}
          </div>
          <div className="flex-shrink-0 text-system-white36 text-xs">{relativeTimeText}</div>
        </div>
        <div className="text-sm">{renderContent()}</div>
      </div>
      {renderImage()}
    </div>
  );
};
